// i18next setup — Phase 4a / Adım 4a.3c.
//
// Phase 0 shipped a self-contained i18next config with ad-hoc locale
// resolution. Adım 4a.3c re-bases that config on the canonical
// @novemai/i18n-sdk locale catalog + resolver per spec 65 + ADR-006:
//
//   * SUPPORTED_LOCALES + DEFAULT_LOCALE + LOCALE_METADATA come from
//     the SDK so adding a locale (e.g., de-DE in Phase 8) requires
//     ONE registration in i18n-sdk/locales.ts — every consumer auto-
//     picks it up.
//
//   * resolveAppLocale() runs the ADR-006 precedence chain with an
//     app-level floor: saklanan seçim → token locale claim'i → firma
//     varsayılanı → tarayıcı → uygulamanın pazar dili. Boot yalnız ilk
//     ve son halkayı bilebilir (claim oturumla birlikte sonra gelir);
//     useApplyResolvedLocale claim çözülünce zinciri yeniden koşturur.
//
//   * i18next interpolation stays at {{var}} for now; the canonical
//     ICU MF2 formatter from @novemai/i18n-sdk's format() ships in
//     Phase 4b when the component library starts using plural/select
//     selectors — at that point the i18next postProcess hook routes
//     bundle entries through format() instead of i18next's built-in
//     interpolator. Migration is mechanical (`{{var}}` → `{var}`).

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import {
  DEFAULT_LOCALE,
  readCachedResolvedLocale,
  readStoredLocale,
  resolveAppLocale,
  SUPPORTED_LOCALES,
  type LocaleCode,
} from "@novemai/i18n-sdk";

import { localeStorage } from "./locale-storage.ts";
import { turkishPossessiveSuffix } from "./turkish-suffix.ts";

import enUS from "./locales/en-US.json";
import trTR from "./locales/tr-TR.json";

// Re-export from the SDK so existing customer-web consumers
// (router/auth/etc.) keep importing from this module without
// having to learn about a new package.
export { DEFAULT_LOCALE, SUPPORTED_LOCALES };
export type { LocaleCode };

// The customer app SHIPS tr-TR FIRST (ADR-014 §6 — the domain is TR-HR;
// gap analysis §5.3). en-US stays the SDK source/fallback locale (used for
// AI prompt assembly + missing-key fallback), but the app's MARKET default
// when no other signal applies is tr-TR — not the SDK's source DEFAULT_LOCALE.
export const CUSTOMER_APP_DEFAULT_LOCALE: LocaleCode = "tr-TR";

/**
 * Boot dili. Token claim'i burada YOK — /v1/me ancak oturum çözüldükten sonra
 * gelir; onu useApplyResolvedLocale sonradan uygular.
 *
 * `userPref` yuvasına claim'in ÖNBELLEĞİ verilir: giriş ekranında TR seçen
 * kullanıcı, önbellek olmadan her yüklemede önce İngilizce bir ekran görüp
 * /v1/me gelince dilin değişmesini izliyordu (tarayıcı en-US diyor, zincirde
 * onu yenecek bir aday yok). Önbellek claim'in duracağı yuvada durur, yani
 * ilk boyama claim'in ne diyeceğinin tahminidir; claim gerçekten farklıysa
 * hook yine üstüne yazar.
 */
function detectInitialLocale(): LocaleCode {
  const stored = readStoredLocale(localeStorage);
  const cachedClaim = readCachedResolvedLocale(localeStorage);
  if (typeof navigator === "undefined") {
    return stored ?? cachedClaim ?? CUSTOMER_APP_DEFAULT_LOCALE;
  }
  const browserPreferences =
    navigator.languages !== undefined && navigator.languages.length > 0
      ? Array.from(navigator.languages)
      : [navigator.language ?? ""];
  return resolveAppLocale({
    stored,
    userPref: cachedClaim,
    browserPreferences,
    appDefault: CUSTOMER_APP_DEFAULT_LOCALE,
  });
}

export async function initI18n(): Promise<typeof i18n> {
  if (i18n.isInitialized) return i18n;
  await i18n.use(initReactI18next).init({
    lng: detectInitialLocale(),
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    defaultNS: "common",
    ns: ["common"],
    resources: {
      "en-US": { common: enUS },
      "tr-TR": { common: trTR },
    },
    interpolation: {
      // React already escapes — i18next double-escape causes mojibake.
      escapeValue: false,
    },
    returnEmptyString: false,
    returnNull: false,
  });
  // Registered on the instance rather than in `interpolation.format` so it sits
  // beside i18next's built-in formatters instead of replacing them.
  i18n.services.formatter?.add("possessive_tr", (value: unknown) =>
    turkishPossessiveSuffix(typeof value === "number" || typeof value === "string" ? value : ""),
  );
  return i18n;
}

export { i18n };
export default i18n;
